/**
 * Internal Links Component
 * Smart internal linking for SEO and navigation
 */

import React from 'react';
import { Link } from 'react-router-dom';
import BookAppointmentLink from './BookAppointmentLink';
import { LocationData, BlogPost, ServiceData, locations, blogPosts, services } from '../data/seo';
import { getRelatedLocations, getRelatedPosts } from '../utils/seo';

// ============================================================================
// RELATED LOCATIONS COMPONENT
// ============================================================================

interface RelatedLocationsProps {
  currentLocation?: LocationData;
  limit?: number;
  title?: string;
  className?: string;
}

export const RelatedLocations: React.FC<RelatedLocationsProps> = ({
  currentLocation,
  limit = 4,
  title = 'We Also Serve',
  className = '',
}) => {
  const relatedLocations = currentLocation 
    ? getRelatedLocations(currentLocation, locations, limit)
    : locations.slice(0, limit);

  if (relatedLocations.length === 0) return null;

  return (
    <div className={`bg-gray-50 rounded-xl p-6 ${className}`}>
      <h3 className="text-lg font-bold text-primary mb-4">{title}</h3>
      <div className="grid grid-cols-2 gap-3">
        {relatedLocations.map((location) => (
          <Link
            key={location.slug}
            to={`/locations/${location.slug}`}
            className="flex items-center gap-2 text-gray-700 hover:text-brand-teal transition-colors group"
          >
            <i className="fas fa-map-marker-alt text-brand-teal text-sm group-hover:scale-110 transition-transform"></i>
            <span className="text-sm font-medium">{location.name}</span>
          </Link>
        ))}
      </div>
      <Link 
        to="/south-florida-pet-dental"
        className="inline-block mt-4 text-brand-teal font-semibold text-sm hover:underline"
      >
        View All Locations →
      </Link>
    </div>
  );
};

// ============================================================================
// RELATED BLOG POSTS COMPONENT
// ============================================================================

interface RelatedPostsProps {
  currentPost?: BlogPost;
  limit?: number;
  title?: string;
  className?: string;
}

export const RelatedPosts: React.FC<RelatedPostsProps> = ({
  currentPost,
  limit = 3,
  title = 'Related Articles',
  className = '',
}) => {
  const relatedPosts = currentPost
    ? getRelatedPosts(currentPost, blogPosts, limit)
    : blogPosts.filter(p => p.featured).slice(0, limit);

  if (relatedPosts.length === 0) return null;

  return (
    <div className={`${className}`}>
      <h3 className="text-xl font-bold text-primary mb-6">{title}</h3>
      <div className="grid gap-4">
        {relatedPosts.map((post) => (
          <Link
            key={post.slug}
            to={`/blog/${post.slug}`}
            className="group bg-white rounded-lg p-4 shadow-sm hover:shadow-md transition-all border border-gray-100"
          >
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-16 h-16 bg-brand-teal/10 rounded-lg flex items-center justify-center">
                <i className="fas fa-tooth text-brand-teal text-xl"></i>
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="text-sm font-semibold text-gray-800 group-hover:text-brand-teal transition-colors line-clamp-2">
                  {post.title}
                </h4>
                <p className="text-xs text-gray-500 mt-1">
                  {post.readTime} • {post.category}
                </p>
              </div>
            </div>
          </Link>
        ))}
      </div>
      <Link 
        to="/blog"
        className="inline-block mt-4 text-brand-teal font-semibold text-sm hover:underline"
      >
        View All Articles →
      </Link>
    </div>
  );
};

// ============================================================================
// SERVICE LINKS COMPONENT
// ============================================================================

interface ServiceLinksProps {
  currentService?: ServiceData;
  limit?: number;
  title?: string;
  className?: string;
}

export const ServiceLinks: React.FC<ServiceLinksProps> = ({
  currentService,
  limit = 3,
  title = 'Our Services',
  className = '',
}) => {
  const displayServices = currentService
    ? services.filter(s => s.slug !== currentService.slug).slice(0, limit)
    : services.slice(0, limit);

  return (
    <div className={`${className}`}>
      <h3 className="text-lg font-bold text-primary mb-4">{title}</h3>
      <div className="space-y-3">
        {displayServices.map((service) => (
          <Link
            key={service.slug}
            to="/services"
            className="flex items-start gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors group"
          >
            <div className="flex-shrink-0 w-10 h-10 bg-brand-teal/10 rounded-full flex items-center justify-center">
              <i className="fas fa-tooth text-brand-teal"></i>
            </div>
            <div>
              <h4 className="font-semibold text-gray-800 group-hover:text-brand-teal transition-colors">
                {service.name}
              </h4>
              <p className="text-sm text-gray-500">{service.price}</p>
            </div>
          </Link>
        ))}
      </div>
      <Link 
        to="/services"
        className="inline-block mt-4 text-brand-teal font-semibold text-sm hover:underline"
      >
        View All Services →
      </Link>
    </div>
  );
};

// ============================================================================
// QUICK LINKS FOOTER COMPONENT
// ============================================================================

interface QuickLinksProps {
  className?: string;
}

export const QuickLinks: React.FC<QuickLinksProps> = ({ className = '' }) => {
  const popularLocations = locations.slice(0, 6);
  const featuredPosts = blogPosts.filter(p => p.featured).slice(0, 3);

  return (
    <div className={`grid md:grid-cols-3 gap-8 ${className}`}>
      {/* Services Column */}
      <div>
        <h4 className="text-lg font-bold text-gray-800 mb-4">Our Services</h4>
        <ul className="space-y-2">
          {services.map((service) => (
            <li key={service.slug}>
              <Link
                to="/services"
                className="text-gray-600 hover:text-brand-teal transition-colors text-sm"
              >
                {service.name}
              </Link>
            </li>
          ))}
        </ul>
      </div>

      {/* Locations Column */}
      <div>
        <h4 className="text-lg font-bold text-gray-800 mb-4">Locations</h4>
        <ul className="space-y-2">
          {popularLocations.map((location) => (
            <li key={location.slug}>
              <Link
                to={`/locations/${location.slug}`}
                className="text-gray-600 hover:text-brand-teal transition-colors text-sm"
              >
                Pet Dentist {location.name}
              </Link>
            </li>
          ))}
        </ul>
      </div>

      {/* Blog Column */}
      <div>
        <h4 className="text-lg font-bold text-gray-800 mb-4">Pet Dental Tips</h4>
        <ul className="space-y-2">
          {featuredPosts.map((post) => (
            <li key={post.slug}>
              <Link
                to={`/blog/${post.slug}`}
                className="text-gray-600 hover:text-brand-teal transition-colors text-sm line-clamp-1"
              >
                {post.title}
              </Link>
            </li>
          ))}
          <li>
            <Link
              to="/blog"
              className="text-brand-teal font-semibold text-sm hover:underline"
            >
              More Articles →
            </Link>
          </li>
        </ul>
      </div>
    </div>
  );
};

// ============================================================================
// CONTEXTUAL CTA COMPONENT
// ============================================================================

interface ContextualCTAProps {
  location?: LocationData;
  service?: ServiceData;
  variant?: 'inline' | 'box';
  className?: string;
}

export const ContextualCTA: React.FC<ContextualCTAProps> = ({
  location,
  service,
  variant = 'box',
  className = '',
}) => {
  const locationText = location ? ` in ${location.name}` : ' in South Florida';
  const serviceText = service ? service.name : 'dental cleaning';

  if (variant === 'inline') {
    return (
      <p className={`text-gray-700 ${className}`}>
        Ready to schedule your pet's {serviceText}{locationText}?{' '}
        <BookAppointmentLink className="text-brand-teal font-semibold hover:underline">
          Book now
        </BookAppointmentLink>{' '}
        or call{' '}
        <a href="tel:+19544663775" className="text-brand-teal font-semibold hover:underline">
          (954) 466-3775
        </a>.
      </p>
    );
  }

  return (
    <div className={`bg-gradient-to-r from-brand-teal to-teal-600 rounded-xl p-6 text-white text-center ${className}`}>
      <h3 className="text-xl font-bold mb-2">
        Schedule Your Pet's {serviceText}{locationText}
      </h3>
      <p className="opacity-90 mb-4">
        Discuss your pet’s dental care with our veterinary team.
      </p>
      <div className="flex flex-col sm:flex-row gap-3 justify-center">
        <BookAppointmentLink
          className="bg-white text-brand-teal px-6 py-3 rounded-lg font-bold hover:bg-gray-100 transition-colors"
        >
          Book Online
        </BookAppointmentLink>
        <a
          href="tel:+19544663775"
          className="border-2 border-white text-white px-6 py-3 rounded-lg font-bold hover:bg-white hover:text-brand-teal transition-colors"
        >
          <i className="fas fa-phone mr-2"></i>
          (954) 466-3775
        </a>
      </div>
    </div>
  );
};

const InternalLinks = {
  RelatedLocations,
  RelatedPosts,
  ServiceLinks,
  QuickLinks,
  ContextualCTA,
};

export default InternalLinks;



